import { useState, useMemo } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@radix-ui/react-avatar";
import clsx from "clsx";
import { AlertCircle, CircleSlash, Loader2 } from "lucide-react";

import type { TPartImage } from "~/database/types";
import { API_URL } from "~/lib/constants";

interface PartImageProps {
    image?:
        | (Pick<TPartImage, "id" | "status"> & {
              signedUrl?: string | null;
          })
        | null;
    size?: "xs" | "sm" | "md" | "lg" | "full";
    className?: string;
}

const sizeMap = {
    // AIDEV-NOTE: xs (48px) ≈ two lines of body text tall — used as the mobile
    // left-column thumbnail on commitment cards.
    xs: {
        container:
            "w-12 h-12 min-w-12 min-h-12 flex items-center justify-center bg-gray-900/10",
        image: "rounded-lg",
        icon: "h-3 w-3",
    },
    sm: {
        container:
            "w-16 h-16 min-w-16 min-h-16 flex items-center justify-center bg-gray-900/10",
        image: "rounded-lg",
        icon: "h-3 w-3",
    },
    md: {
        container:
            "w-24 h-24 min-w-24 min-h-24 flex items-center justify-center bg-gray-900/10",
        image: "rounded-lg",
        icon: "h-4 w-4",
    },
    lg: {
        container:
            "w-32 h-32 min-w-32 min-h-32 flex items-center justify-center",
        image: "rounded-lg",
        icon: "h-4 w-4",
    },
    full: {
        container: "w-full h-full",
        image: "w-full h-full object-cover",
        icon: "h-4 w-4",
    },
};

export function PartImage({ image, size = "lg", className }: PartImageProps) {
    const classes = sizeMap[size];
    const [shouldUseProxy, setUseProxy] = useState(false);

    // AIDEV-NOTE: Stable cache buster based on image ID and signed URL to prevent flickering
    // Only changes when the actual image changes, not on every render
    const cacheBuster = useMemo(() => {
        if (!image?.signedUrl) return "";

        // Use a combination of image ID and URL hash for stable cache busting
        const urlHash = image.signedUrl.split("?")[0].slice(-20); // Last 20 chars of base URL
        return `${image.id}-${urlHash}`.replace(/[^a-zA-Z0-9-]/g, "");
    }, [image?.id, image?.signedUrl]);

    // AIDEV-NOTE: All hooks must be called before any early returns (React Rules of Hooks)
    // This useMemo must be called unconditionally even when image is null
    const imageSrc = useMemo(() => {
        if (!image?.signedUrl) return "";

        if (shouldUseProxy && image.id) {
            // Use stable cache buster for proxy endpoint
            const proxyUrl = `${API_URL}/image/${image.id}/proxy?v=${cacheBuster}`;
            return proxyUrl;
        }

        // AIDEV-NOTE: Use stable cache buster to prevent flickering while ensuring new images load
        const url = new URL(image.signedUrl);
        url.searchParams.set("cb", cacheBuster);
        const directUrl = url.toString();
        return directUrl;
    }, [image?.signedUrl, shouldUseProxy, image?.id, cacheBuster]);

    if (!image) return null;

    // AIDEV-NOTE: Fallback to proxy endpoint when direct image load fails (BLOCKED_BY_ORB)
    const handleImageError = () => {
        if (!shouldUseProxy && image.signedUrl) {
            setUseProxy(true);
        }
    };

    return (
        <Avatar className={clsx("rounded-md", classes.container, className)}>
            {image.status === "COMPLETED" ? (
                <AvatarImage
                    className={clsx(classes.image)}
                    src={imageSrc}
                    onError={handleImageError}
                />
            ) : image.status === "ERROR" ? (
                <AvatarFallback
                    className={clsx(
                        size === "full"
                            ? "w-full h-full flex items-center justify-center"
                            : "m-auto inline-block",
                    )}
                >
                    <AlertCircle className={classes.icon} />
                </AvatarFallback>
            ) : image.status === "SKIPPED" ? (
                <AvatarFallback
                    className={clsx(
                        size === "full"
                            ? "w-full h-full flex items-center justify-center"
                            : "m-auto inline-block",
                    )}
                >
                    <CircleSlash className={classes.icon} />
                </AvatarFallback>
            ) : (
                <AvatarFallback
                    className={clsx(
                        size === "full"
                            ? "w-full h-full flex items-center justify-center"
                            : "m-auto inline-block",
                    )}
                >
                    <Loader2 className={clsx(classes.icon, "animate-spin")} />
                </AvatarFallback>
            )}
        </Avatar>
    );
}
